import { z } from 'zod';
import { emailSchema, orgRoleSchema, platformRoleSchema } from './auth';
import { phoneNumberSchema } from './alerts';
import { languageSchema, objectIdSchema } from './common';

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a #RRGGBB colour');

/** Pages a role can be sent to right after sign-in (Home when the role cannot open the chosen one). */
export const START_PAGES = ['home', 'live', 'dashboards', 'schematics', 'assets'] as const;
export type StartPage = (typeof START_PAGES)[number];

export const orgSettingsSchema = z.object({
  timeZone: z.string().min(1).max(64).default('Africa/Tunis'),
  language: languageSchema.default('en'),
  unitSystemId: z.string().max(64).default('field'),
  liveIntervalSec: z.number().int().min(2).max(60).default(5),
  requireMfa: z.boolean().default(false),
  clientExport: z.boolean().default(false),
  editorsCanPublishToClients: z.boolean().default(false),
  maxQueryRangeDays: z.number().int().min(1).max(3660).default(400),
  sessionMaxAgeDays: z.number().int().min(1).max(90).default(14),
  inviteDomains: z.array(z.string().trim().toLowerCase().min(3).max(253)).max(50).default([]),
  allowOrgAdminImpersonation: z.boolean().default(false),
  defaultLiveDashboardId: objectIdSchema.nullable().default(null),
  defaultDecimals: z.record(z.string().max(64), z.number().int().min(0).max(8)).default({}),
  unitOverrides: z.record(z.string().max(64), z.string().max(64)).default({}),
  lineColors: z.record(z.string().max(32), hexColor).default({}),
  atmosphericPressurePa: z.number().min(50_000).max(110_000).default(101_325),
  /** The page each role opens after sign-in; a role not listed starts on Home. */
  startPages: z
    .object({ org_admin: z.enum(START_PAGES), editor: z.enum(START_PAGES), viewer: z.enum(START_PAGES), client: z.enum(START_PAGES) })
    .partial()
    .strict()
    .default({}),
});
export type OrgSettings = z.infer<typeof orgSettingsSchema>;

export const orgPlanSchema = z.object({
  name: z.string().trim().min(1).max(64).default('standard'),
  maxUsers: z.number().int().min(1).max(10_000).default(25),
  maxClientUsers: z.number().int().min(0).max(10_000).default(25),
  maxDataSources: z.number().int().min(0).max(1000).default(5),
  maxDashboards: z.number().int().min(0).max(100_000).default(200),
  /** Dyia, the assistant (doc 13), is available to this organization. */
  assistant: z.boolean().default(false),
});
export type OrgPlan = z.infer<typeof orgPlanSchema>;

export const orgBrandingSchema = z.object({
  logoFileId: objectIdSchema.nullable().default(null),
  primaryColor: hexColor.default('#3454d1'),
});

export const orgStatusSchema = z.enum(['active', 'suspended', 'deleted']);

// ---- Organization units (doc 05 §1.5, §2) ----

/** A linear unit an organization added to an existing quantity; `factor`/`offset` convert to the quantity's base unit. */
export const customUnitSchema = z.object({
  id: z.string().max(64),
  symbol: z.string(),
  name: z.string(),
  quantity: z.string(),
  factor: z.number(),
  offset: z.number(),
});
export type CustomUnit = z.infer<typeof customUnitSchema>;

/** How the admin defines one: "1 of mine = factor × <relativeTo> + offset". */
export const customUnitInputSchema = z.object({
  symbol: z.string().trim().min(1).max(16),
  name: z.string().trim().min(1).max(64),
  quantity: z.string().min(1).max(64),
  relativeTo: z.string().min(1).max(64),
  factor: z.number().finite().refine((v) => v !== 0, 'The factor cannot be zero'),
  offset: z.number().finite().default(0),
});
export type CustomUnitRequest = z.infer<typeof customUnitInputSchema>;

export const customUnitSystemSchema = z.object({
  key: z.string().max(64),
  name: z.string(),
  defaults: z.record(z.string().max(64), z.string().max(64)),
});
export type CustomUnitSystem = z.infer<typeof customUnitSystemSchema>;

export const customUnitSystemInputSchema = z.object({
  name: z.string().trim().min(1).max(64),
  defaults: z.record(z.string().max(64), z.string().max(64)),
});
export type CustomUnitSystemInput = z.infer<typeof customUnitSystemInputSchema>;

export const orgUnitsResponseSchema = z.object({ units: z.array(customUnitSchema), unitSystems: z.array(customUnitSystemSchema) });
export type OrgUnitsResponse = z.infer<typeof orgUnitsResponseSchema>;

// ---- Files (doc 08 §6) ----

export const uploadFileRequestSchema = z.object({
  kind: z.enum(['logo', 'image']),
  name: z.string().trim().min(1).max(200),
  /** The browser reads the file; no multipart upload is involved. */
  dataBase64: z.string().min(4).max(3_000_000),
});
export type UploadFileRequest = z.infer<typeof uploadFileRequestSchema>;

export const fileSchema = z.object({ id: objectIdSchema, kind: z.enum(['logo', 'image']), name: z.string(), mime: z.string(), size: z.number() });
export type FileInfo = z.infer<typeof fileSchema>;

/** What any member (or platform user) gets from `GET /orgs/:orgId`. */
export const orgContextResponseSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  slug: z.string(),
  status: orgStatusSchema,
  branding: orgBrandingSchema,
  settings: orgSettingsSchema,
  /** Units and unit systems the organization added to the global library. */
  customUnits: z.array(customUnitSchema).default([]),
  customUnitSystems: z.array(customUnitSystemSchema).default([]),
  /** The caller's org role; null for platform users acting inside the org. */
  role: orgRoleSchema.nullable(),
  permissions: z.array(z.string()),
  /** Dyia can be used here by the caller: switched on by SmarDac, for this organization and this role. */
  assistant: z.boolean().default(false),
  /** This server is connected to SmarDac Messenger: supervisors can be asked on WhatsApp (doc 14). */
  whatsapp: z.boolean().default(false),
  /** Seat usage; only present for callers with `org.users.manage`. */
  seats: z
    .object({ users: z.number(), maxUsers: z.number(), clients: z.number(), maxClientUsers: z.number() })
    .nullable(),
});
export type OrgContextResponse = z.infer<typeof orgContextResponseSchema>;

export const updateOrgSettingsRequestSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  branding: orgBrandingSchema.partial().optional(),
  settings: orgSettingsSchema.partial().optional(),
});
export type UpdateOrgSettingsRequest = z.infer<typeof updateOrgSettingsRequestSchema>;

// ---- Members ----

export const memberSchema = z.object({
  userId: objectIdSchema,
  name: z.string(),
  email: z.string(),
  role: orgRoleSchema,
  status: z.enum(['invited', 'active', 'suspended']),
  clientExport: z.boolean().nullable(),
  mfaEnabled: z.boolean(),
  lastLoginAt: z.string().nullable(),
  joinedAt: z.string().nullable(),
  createdAt: z.string(),
  /** WhatsApp number of the person (doc 14). */
  phone: z.string().nullable().default(null),
});
export type Member = z.infer<typeof memberSchema>;

export const updateMemberRequestSchema = z
  .object({
    role: orgRoleSchema.optional(),
    status: z.enum(['active', 'suspended']).optional(),
    clientExport: z.boolean().nullable().optional(),
    /** WhatsApp number; null removes it. Entered by an Org Admin, not verified (doc 14 §2.1). */
    phone: phoneNumberSchema.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type UpdateMemberRequest = z.infer<typeof updateMemberRequestSchema>;

export const directoryEntrySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  email: z.string(),
  role: orgRoleSchema,
});
export type DirectoryEntry = z.infer<typeof directoryEntrySchema>;

export const grantNodeSchema = z.object({
  nodeType: z.enum(['field', 'well']),
  nodeId: objectIdSchema,
});
export type GrantNode = z.infer<typeof grantNodeSchema>;

export const createInvitationsRequestSchema = z.object({
  emails: z.array(emailSchema).min(1).max(50),
  role: orgRoleSchema,
  grants: z.array(grantNodeSchema).max(500).default([]),
  message: z.string().trim().max(1000).optional(),
  language: languageSchema.optional(),
  /** Required to grant a whole field to a Client (doc 02 §3.5). */
  confirmClientFieldGrant: z.boolean().optional(),
});
export type CreateInvitationsRequest = z.infer<typeof createInvitationsRequestSchema>;

export const invitationSchema = z.object({
  id: objectIdSchema,
  email: z.string(),
  role: orgRoleSchema,
  status: z.enum(['pending', 'accepted', 'revoked', 'expired']),
  expiresAt: z.string(),
  createdAt: z.string(),
  invitedByName: z.string(),
});
export type Invitation = z.infer<typeof invitationSchema>;

export const createInvitationsResponseSchema = z.object({
  created: z.array(invitationSchema),
  skipped: z.array(z.object({ email: z.string(), reason: z.string() })),
});
export type CreateInvitationsResponse = z.infer<typeof createInvitationsResponseSchema>;

export const impersonateRequestSchema = z.object({ userId: objectIdSchema });
export const platformImpersonateRequestSchema = z.object({ orgId: objectIdSchema, userId: objectIdSchema });

// ---- Audit ----

export const auditEventSchema = z.object({
  id: objectIdSchema,
  orgId: objectIdSchema.nullable(),
  at: z.string(),
  actorUserId: objectIdSchema.nullable(),
  actorName: z.string(),
  actorType: z.enum(['user', 'platform', 'system']),
  impersonatorId: objectIdSchema.nullable(),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string().nullable(),
  summary: z.string(),
  diff: z.unknown().optional(),
  ip: z.string(),
  requestId: z.string(),
});
export type AuditEvent = z.infer<typeof auditEventSchema>;

export const auditQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(200).optional(),
  actor: objectIdSchema.optional(),
  action: z.string().max(64).optional(),
  targetType: z.string().max(64).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  format: z.enum(['json', 'csv']).default('json'),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

// ---- Platform console ----

export const platformOrgSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  slug: z.string(),
  status: orgStatusSchema,
  plan: orgPlanSchema,
  members: z.number(),
  clients: z.number(),
  createdAt: z.string(),
});
export type PlatformOrg = z.infer<typeof platformOrgSchema>;

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9](?:[a-z0-9-]{1,46}[a-z0-9])$/, 'Use 3–48 lowercase letters, digits or dashes');

export const createOrgRequestSchema = z.object({
  name: z.string().trim().min(2).max(120),
  slug: slugSchema,
  plan: orgPlanSchema.partial().optional(),
  firstAdminEmail: emailSchema,
});
export type CreateOrgRequest = z.infer<typeof createOrgRequestSchema>;

export const updateOrgRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    status: z.enum(['active', 'suspended']).optional(),
    plan: orgPlanSchema.partial().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type UpdateOrgRequest = z.infer<typeof updateOrgRequestSchema>;

export const platformUserSchema = z.object({
  id: objectIdSchema,
  email: z.string(),
  name: z.string(),
  platformRole: platformRoleSchema.nullable(),
  status: z.enum(['active', 'locked', 'disabled']),
  mfaEnabled: z.boolean(),
  lockedUntil: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  pending: z.boolean(),
});
export type PlatformUser = z.infer<typeof platformUserSchema>;

export const createPlatformUserRequestSchema = z.object({
  email: emailSchema,
  name: z.string().trim().min(1).max(120),
  platformRole: platformRoleSchema,
});
export type CreatePlatformUserRequest = z.infer<typeof createPlatformUserRequestSchema>;

export const updatePlatformUserRequestSchema = z
  .object({
    platformRole: platformRoleSchema.nullable().optional(),
    status: z.enum(['active', 'disabled']).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type UpdatePlatformUserRequest = z.infer<typeof updatePlatformUserRequestSchema>;

// ---- Platform health (doc 02 §11) ----

export const platformHealthSchema = z.object({
  api: z.object({ version: z.string(), uptimeSec: z.number(), mongo: z.boolean(), redis: z.boolean() }),
  /** Sockets and pollers of the API instance that answered; null when it runs without the hub. */
  realtime: z.object({ sockets: z.number(), pollers: z.number() }).nullable(),
  mailQueue: z.object({ waiting: z.number(), failed: z.number() }),
  dataSources: z.array(
    z.object({
      id: objectIdSchema,
      orgId: objectIdSchema,
      orgName: z.string(),
      name: z.string(),
      kind: z.enum(['influxdb2', 'mongodb']),
      status: z.enum(['ok', 'error', 'paused', 'unknown']),
      lastSuccessAt: z.string().nullable(),
      lastError: z.string().nullable(),
      lastErrorAt: z.string().nullable(),
      /** Share of failed calls among the last 200 on this instance; null before any call. */
      errorRate: z.number().nullable(),
      p95Ms: z.number().nullable(),
      calls: z.number(),
      circuitOpen: z.boolean(),
    }),
  ),
});
export type PlatformHealth = z.infer<typeof platformHealthSchema>;
